import { useCallback, useEffect } from 'react';
import { type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import type { EstatesFiltersSchemaType } from '../schemas';

const DATE_RANGE_ERROR = 'dateRange';

/** Marks the created / updated ranges as invalid while "from" is after "to" */
export const useEstateFilterDateValidation = () => {
  const { t } = useTranslation();
  const methods = useFormContext<EstatesFiltersSchemaType>();
  const { setError, clearErrors, watch, getFieldState } = methods;

  const createdAt = watch(['createdAt.min', 'createdAt.max']);
  const updatedAt = watch(['updatedAt.min', 'updatedAt.max']);

  const validateDateRange = useCallback(
    ([from, to]: Array<Date | string | undefined>, fields: Path<EstatesFiltersSchemaType>[]) => {
      // A range error goes away as soon as one side is empty, format errors stay
      if (!from || !to) {
        fields.forEach((field) => {
          if (getFieldState(field).error?.type === DATE_RANGE_ERROR) clearErrors(field);
        });
        return true;
      }

      const isValid = dayjs(from).isBefore(dayjs(to)) || dayjs(from).isSame(dayjs(to));
      if (!isValid) {
        fields.forEach((field) => {
          setError(field, {
            type: DATE_RANGE_ERROR,
            message: t('invalidDateRange'),
          });
        });
      } else {
        fields.forEach((field) => {
          if (getFieldState(field).error?.type === DATE_RANGE_ERROR) clearErrors(field);
        });
      }
      return true;
    },
    [setError, clearErrors, getFieldState, t]
  );

  useEffect(() => {
    validateDateRange(createdAt, ['createdAt.min', 'createdAt.max']);
  }, [validateDateRange, createdAt]);

  useEffect(() => {
    validateDateRange(updatedAt, ['updatedAt.min', 'updatedAt.max']);
  }, [validateDateRange, updatedAt]);
};
